import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, Edit, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { formatCurrency, getDefaultQuoteData, denormalizeExclusions } from '../../lib/defaultData';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Quotes belonging to a single project. */
export default function ProjectQuotesTab({ project }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/quotes`, { params: { project_id: project.id } })
      .then(res => { if (!cancelled) setQuotes(res.data?.data || []); })
      .catch(err => console.error('Failed to fetch project quotes:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [project.id]);

  const handleNewQuote = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const numRes = await axios.get(`${API}/quotes/next-number`);
      const data = getDefaultQuoteData();
      const { id: _id, ...rest } = data;
      const { logo_fallback_url: _lfu, ...companyInfo } = rest.company_info;
      const payload = {
        ...rest,
        company_info: companyInfo,
        exclusions: denormalizeExclusions(rest.exclusions),
        quote_no: numRes.data.quote_no,
        date: new Date().toISOString().slice(0, 10),
        // Link + prefill from the project
        project_id: project.id,
        project_name: project.project_name || '',
        project_address: project.site_address || '',
        client_id: project.client_id || '',
        client_name: project.client_name || '',
      };
      const res = await axios.post(`${API}/quotes`, payload);
      navigate(`/quote/${res.data.id}?from_project=${project.id}`);
    } catch (err) {
      console.error('Failed to create quote:', err);
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading...
      </div>
    );
  }

  if (quotes.length === 0) {
    return (
      <div className="text-center py-16">
        <FileText className="w-16 h-16 mx-auto text-[#c9a84c]/40 mb-4" />
        <p className="text-[#6b7280] text-lg mb-2">No quotes for this project</p>
        <p className="text-[#6b7280]/60 text-sm mb-6">Quotes created here will be linked to {project.project_name}</p>
        <Button onClick={handleNewQuote} disabled={creating} className="bg-[#1a1a2e] text-white hover:bg-[#252545]">
          {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          {creating ? 'Creating...' : 'New Quote'}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Quotes ({quotes.length})
        </h3>
        <Button
          onClick={handleNewQuote}
          disabled={creating}
          size="sm"
          className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
        >
          {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          {creating ? 'Creating...' : 'New Quote'}
        </Button>
      </div>

      <div className="space-y-1">
        <div
          className="hidden sm:grid gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]"
          style={{ gridTemplateColumns: '130px 110px 1fr 130px 60px' }}
        >
          <span>Quote No</span>
          <span>Date</span>
          <span>Client</span>
          <span className="text-right">Subtotal</span>
          <span className="text-right">Open</span>
        </div>

        {quotes.map(q => (
          <div
            key={q.id}
            className="grid gap-3 px-4 py-3 rounded-md hover:bg-[#f3f2ef] transition-colors cursor-pointer items-center"
            style={{
              gridTemplateColumns: '130px 110px 1fr 130px 60px',
              borderBottom: '1px solid rgba(201,168,76,0.1)',
            }}
            onClick={() => navigate(`/quote/${q.id}?from_project=${project.id}`)}
          >
            <span className="font-mono font-semibold text-sm text-[#1a1a2e]">{q.quote_no || '—'}</span>
            <span className="text-sm text-[#6b7280]">{q.date || '—'}</span>
            <span className="text-sm truncate">{q.client_name || '—'}</span>
            <span className="text-sm font-mono text-right font-medium">
              {q.subtotal != null ? formatCurrency(q.subtotal) : '—'}
            </span>
            <div className="flex items-center justify-end">
              <Edit className="w-4 h-4 text-[#6b7280]" />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
